import { scanStructure, textOf } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/**
 * Delete a `}` or `]` that closes nothing: `{"a":1}}`, `[1,2]]`.
 *
 * "Closes nothing" is decided by the container scan, not by counting characters: the token
 * has to sit at depth zero, after a complete root value, in the run of closers that
 * immediately follows it. That is deliberately narrow. The legacy rules
 * `/}{2,}$/ → '}'` and `/\]{2,}$/ → ']'` collapsed any run of closing brackets at the end of
 * the document and destroyed `[[1,2],[3,4]]` — **valid JSON** — into `[1,2],[3,4]` while
 * reporting three phantom fixes for `{"a":{"b":{"c":1}}}` (recon §3.2, A8/A9). Here those
 * two documents produce no proposal at all, because their final brackets close something.
 *
 * WHICH DIAGNOSTIC THIS CLOSES — corrected 2026-09-14, when lint and repair were first run
 * together. The file used to declare `resolves: ['syntax/unexpected-token']` at the rule
 * level while its proposals resolved `syntax/close-brace-expected` /
 * `syntax/close-bracket-expected`: three different answers in one file, and the last two mean
 * the OPPOSITE defect — "this container is never closed, the bracket is missing" — from the
 * one this rule fixes, which is a bracket that is surplus.
 *
 * What the linter actually emits for `{"a":1}}` is `syntax/trailing-content`, measured on
 * `{"a":1}}`, `[1,2]]`, `{"a":{"b":1}}}` and `[[1,2],[3,4]]]`: a closer past the end of a
 * complete root value is content after the document, which is exactly what it is. Because
 * the codes did not match, the diagnostic came out of the pipeline with no `fix` attached and
 * the user saw an error that a safe one-character rule could have closed on the spot.
 */
export const dropExtraClosersRule: RepairRule = {
  id: 'drop-extra-closers',
  confidence: 'safe',
  priority: 51,
  resolves: ['syntax/trailing-content'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    return scanStructure(ctx.source, ctx.tokens).extraClosers.map((token) => ({
      ruleId: 'drop-extra-closers',
      span: { from: token.from, to: token.to },
      edits: [{ from: token.from, to: token.to, insert: '' }],
      labelCode: 'fix.drop-extra-closers',
      labelParams: { bracket: textOf(ctx.source, token) },
      resolves: 'syntax/trailing-content' as const,
    }));
  },
};
